/**
 * Абоненты СНТ — разметка 1:1 с legacy admin_snt_users.tpl
 * (desktop-таблица .sntusers + мобильные таблицы, оба варианта редактируемые).
 *
 * Inline-JS шаблона перенесён в обработчики React с сохранением поведения:
 *  - валидация «на лету»: логин /^[a-zA-Z0-9]{1,20}$/, пароль /^[a-zA-Z0-9!-^]{1,20}$/,
 *    email — регуляркой шаблона; поле красится #e727274a (ошибка) / #9dff9d (ок),
 *    при ошибке сохранение строки не выполняется (legacy-флаг action_desk, здесь — по строке);
 *  - focusout по изменённому полю → confirm-модал «Подтверждаете изменение данных?»
 *    (Подтвердить → PUT, Отмена → возврат прежнего значения, фон #fff);
 *  - чекбоксы (Кандидат / рассылка Email) → тот же confirm-модал;
 *  - «Кандидат» требует ФИО из трёх слов, иначе модал #myModaldabfio и галка снимается;
 *  - рассылка Email без email → модал #rassl_email.
 *  - Тумблер рассылки SMS убран: канал отключён (rassl_sms при сохранении не меняется).
 *
 * API: GET /api/admin/snt-users, PUT /api/admin/snt-users/{id}
 *      (fio, login, pass, email, tel, rassl_sms, rassl_email, candidate — те же поля,
 *      что слал legacy-AJAX на /admin/snt_users?group=0).
 *
 * ПАРИТЕТ: пароль ЛК хранится и показывается открытым текстом — как в legacy-админке.
 */

import { useEffect, useState } from 'react'

import { api, ApiError } from '@/api/client'
import { Modal } from '@/components/Modal'
import { useApi } from '@/lib/useApi'

/** Строка snt_users (SntUsers::list → SELECT * FROM snt_users ORDER BY position). */
interface UserRow {
  id: number
  chetch_number: string | null
  position: string | null
  fio: string | null
  login: string | null
  pass: string | null
  email: string | null
  tel: string | null
  candidate: string | null // '1' | '0'
  rassl_email: string | null // '1' | '0'
  rassl_sms: string | null // '1' | '0'
}

/** Редактируемые поля строки — ровно те, что шлёт legacy-AJAX. */
interface Draft {
  fio: string
  login: string
  pass: string
  email: string
  tel: string
  candidate: boolean
  rassl_email: boolean
  rassl_sms: boolean
}

type TextField = 'fio' | 'login' | 'pass' | 'email' | 'tel'
type FlagField = 'candidate' | 'rassl_email' | 'rassl_sms'

/** Регулярки из admin_snt_users.tpl (строки 10, 42, 75). */
const LOGIN_PATTERN = /^[a-zA-Z0-9]{1,20}$/i
const PASS_PATTERN = /^[a-zA-Z0-9!-^]{1,20}$/i
const EMAIL_PATTERN = /^([a-z0-9_.-])+@[a-z0-9-]+\.([a-z]{2,4}\.)?[a-z]{2,4}$/i

/** admin.css/newapproach.css — так же, как их подключает admin.tpl (в index.html их нет). */
function useAdminCss(): void {
  useEffect(() => {
    for (const href of ['/assets/css/admin.css', '/assets/css/newapproach.css']) {
      if (document.head.querySelector(`link[href="${href}"]`) !== null) continue
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = href
      document.head.appendChild(link)
    }
  }, [])
}

function toDraft(row: UserRow): Draft {
  return {
    fio: row.fio ?? '',
    login: row.login ?? '',
    pass: row.pass ?? '',
    email: row.email ?? '',
    tel: row.tel ?? '',
    candidate: row.candidate === '1',
    rassl_email: row.rassl_email === '1',
    rassl_sms: row.rassl_sms === '1',
  }
}

/** «Кандидат» доступен только при полных ФИО (legacy: fio.split(' ')[2] != undefined). */
function hasFullName(fio: string): boolean {
  return fio.split(' ').filter((part) => part !== '').length >= 3
}

export function AdminSntUsersPage() {
  useAdminCss()

  const { data } = useApi<UserRow[]>('/api/admin/snt-users')
  const rows = data ?? []

  /** Текущие значения полей. */
  const [drafts, setDrafts] = useState<Record<number, Draft>>({})
  /** Значения, подтверждённые последними (legacy data-value у input). */
  const [saved, setSaved] = useState<Record<number, Draft>>({})
  /** legacy action_desk: при невалидном логине/пароле/email строка не сохраняется. */
  const [canSave, setCanSave] = useState<Record<number, boolean>>({})
  /** Подсветка полей: ключ `${id}:${field}` → цвет фона. */
  const [background, setBackground] = useState<Record<string, string>>({})

  const [errors, setErrors] = useState<string[]>([])
  const [oks, setOks] = useState<string[]>([])

  /** Открытый confirm-модал: что подтверждаем и что делать при отмене. */
  const [confirm, setConfirm] = useState<{ id: number; abort: () => void } | null>(null)
  const [showFioModal, setShowFioModal] = useState(false)
  const [showRasslEmailModal, setShowRasslEmailModal] = useState(false)

  useEffect(() => {
    if (data === null) return
    const initial: Record<number, Draft> = {}
    for (const row of data) initial[row.id] = toDraft(row)
    setDrafts(initial)
    setSaved(initial)
    setCanSave({})
    setBackground({})
  }, [data])

  function setField(id: number, field: TextField, value: string): void {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }))
  }

  function setFlag(id: number, field: FlagField, value: boolean): void {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }))
  }

  function paint(id: number, field: TextField, color: string): void {
    setBackground((prev) => ({ ...prev, [`${id}:${field}`]: color }))
  }

  /** legacy: on('change keyup paste') у login/pass/email — покраска поля + флаг action. */
  function validateField(id: number, field: TextField, value: string): void {
    const pattern =
      field === 'login' ? LOGIN_PATTERN : field === 'pass' ? PASS_PATTERN : field === 'email' ? EMAIL_PATTERN : null
    if (pattern === null) return

    const valid = pattern.test(value)
    paint(id, field, valid ? '#9dff9d' : '#e727274a')
    setCanSave((prev) => ({ ...prev, [id]: valid }))
  }

  /** PUT строки — те же восемь полей, что слал legacy-AJAX. */
  async function save(id: number): Promise<void> {
    const draft = drafts[id]
    if (draft === undefined) return

    if (canSave[id] === false) return // legacy: if (action_desk) { $.ajax(...) }

    setErrors([])
    setOks([])
    try {
      await api.put<UserRow>(`/api/admin/snt-users/${id}`, {
        fio: draft.fio,
        login: draft.login,
        pass: draft.pass,
        email: draft.email,
        tel: draft.tel,
        rassl_sms: draft.rassl_sms ? '1' : '0',
        rassl_email: draft.rassl_email ? '1' : '0',
        candidate: draft.candidate ? '1' : '0',
      })
      setSaved((prev) => ({ ...prev, [id]: draft }))
      // legacy success: $(curinp).css('background-color', '#fff')
      setBackground((prev) => {
        const next = { ...prev }
        for (const field of ['fio', 'login', 'pass', 'email', 'tel']) delete next[`${id}:${field}`]
        return next
      })
    } catch (e: unknown) {
      setErrors([e instanceof ApiError ? e.message : 'Не удалось сохранить абонента'])
    }
  }

  /** focusout текстового поля: значение изменилось → подтверждение. */
  function onFieldBlur(id: number, field: TextField): void {
    const draft = drafts[id]
    const base = saved[id]
    if (draft === undefined || base === undefined) return
    if (draft[field] === base[field]) return

    setConfirm({
      id,
      abort: () => {
        setField(id, field, base[field])
        paint(id, field, '#fff')
      },
    })
  }

  /** Чекбоксы: сначала проверки шаблона (модалы), затем подтверждение. */
  function onFlagChange(id: number, field: FlagField, checked: boolean): void {
    const draft = drafts[id]
    if (draft === undefined) return

    if (checked && field === 'rassl_email' && draft.email === '') {
      setShowRasslEmailModal(true) // legacy: $('#rassl_email').modal() + галка снимается
      return
    }
    if (checked && field === 'candidate' && !hasFullName(draft.fio)) {
      setShowFioModal(true) // legacy: $('#myModaldabfio').modal('show')
      return
    }

    setFlag(id, field, checked)
    setConfirm({ id, abort: () => setFlag(id, field, !checked) })
  }

  function fieldStyle(id: number, field: TextField, extra?: React.CSSProperties): React.CSSProperties {
    const color = background[`${id}:${field}`]
    return color === undefined ? { ...extra } : { ...extra, backgroundColor: color }
  }

  return (
    <>
      {/* Чтобы отметить человека как кандидата, введите полные ФИО (legacy #myModaldabfio — без шапки) */}
      <Modal
        open={showFioModal}
        onClose={() => setShowFioModal(false)}
        dialogStyle={{ width: 450, paddingTop: '15%' }}
        footer={
          <center>
            <button type="button" className="btn btn-default" onClick={() => setShowFioModal(false)}>
              Закрыть
            </button>
          </center>
        }
      >
        Чтобы отметить человека как кандидата, введите полные ФИО.
      </Modal>

      {/* legacy #rassl_email */}
      <Modal
        open={showRasslEmailModal}
        onClose={() => setShowRasslEmailModal(false)}
        title="Внимание!"
        footer={
          <button type="button" className="btn btn-default" onClick={() => setShowRasslEmailModal(false)}>
            Закрыть
          </button>
        }
      >
        Для включения рассылки квитанций на Email, необходимо указать Email!
      </Modal>

      <div className="row">
        <div className="col-xs-12">
          <p>
            <span>
              <b>Логин</b>
            </span>{' '}
            - латинские буквы и цифры.
            <br />
            <span>
              <b>Пароль</b>
            </span>{' '}
            - латинские буквы, цифры и спецсимволы.
          </p>
        </div>
      </div>
      <div className="row">
        <div className="col-xs-6">
          {oks.map((text, index) => (
            <p key={index} className="okke" style={{ float: 'left' }}>
              {text}
            </p>
          ))}
          {errors.map((text, index) => (
            <span key={index} style={{ color: '#ce4844', float: 'left' }}>
              {text}
            </span>
          ))}
        </div>
      </div>
      <div className="row">
        <div className="col-xs-12 hidden-xs" style={{ minWidth: 1200 }}>
          <table className="table table-hover sntusers">
            <thead className="thead-dark">
              <tr>
                <th>№</th>
                <th>№ Счетчика</th>
                <th>Место</th>
                <th>ФИО</th>
                <th>Логин</th>
                <th>Пароль</th>
                <th>Email</th>
                <th>Телефон</th>
                <th style={{ textAlign: 'center' }}>Кандидат</th>
                <th>Рассылка квитанций</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((item, index) => {
                const draft = drafts[item.id] ?? toDraft(item)
                return (
                  <tr key={item.id}>
                    <td>{index + 1}</td>
                    <td>{item.chetch_number}</td>
                    <td>{item.position}</td>
                    <td>
                      <input
                        type="text"
                        name="fio"
                        value={draft.fio}
                        className="form-control desc_inp"
                        autoComplete="off"
                        style={fieldStyle(item.id, 'fio', { height: 25 })}
                        onChange={(event) => setField(item.id, 'fio', event.target.value)}
                        onBlur={() => onFieldBlur(item.id, 'fio')}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="login"
                        value={draft.login}
                        className="form-control desc_inp"
                        autoComplete="off"
                        style={fieldStyle(item.id, 'login', { height: 25 })}
                        onChange={(event) => {
                          setField(item.id, 'login', event.target.value)
                          validateField(item.id, 'login', event.target.value)
                        }}
                        onBlur={() => onFieldBlur(item.id, 'login')}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="pass"
                        value={draft.pass}
                        className="form-control desc_inp"
                        autoComplete="off"
                        style={fieldStyle(item.id, 'pass', { height: 25 })}
                        onChange={(event) => {
                          setField(item.id, 'pass', event.target.value)
                          validateField(item.id, 'pass', event.target.value)
                        }}
                        onBlur={() => onFieldBlur(item.id, 'pass')}
                      />
                    </td>
                    <td>
                      <input
                        type="email"
                        name="email"
                        value={draft.email}
                        className="form-control desc_inp"
                        autoComplete="off"
                        style={fieldStyle(item.id, 'email', { height: 25 })}
                        onChange={(event) => {
                          setField(item.id, 'email', event.target.value)
                          validateField(item.id, 'email', event.target.value)
                        }}
                        onBlur={() => onFieldBlur(item.id, 'email')}
                      />
                    </td>
                    <td>
                      <input
                        type="tel"
                        name="tel"
                        className="phone form-control desc_inp"
                        autoComplete="off"
                        value={draft.tel}
                        style={fieldStyle(item.id, 'tel', { height: 25 })}
                        onChange={(event) => setField(item.id, 'tel', event.target.value)}
                        onBlur={() => onFieldBlur(item.id, 'tel')}
                      />
                    </td>
                    <td>
                      <input
                        className="dcandidate desc_checkbox"
                        type="checkbox"
                        name="candidate"
                        value="1"
                        checked={draft.candidate}
                        onChange={(event) => onFlagChange(item.id, 'candidate', event.target.checked)}
                      />
                    </td>
                    <td>
                      Email
                      <input
                        type="checkbox"
                        className="desc_checkbox"
                        name="rassl_email"
                        value="1"
                        checked={draft.rassl_email}
                        onChange={(event) => onFlagChange(item.id, 'rassl_email', event.target.checked)}
                      />
                    </td>
                    <td>
                      <input type="hidden" name="update_user" value={item.id} readOnly />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <div className="col-xs-12 hidden-sm hidden-md hidden-lg">
          {rows.map((item, index) => {
            const draft = drafts[item.id] ?? toDraft(item)
            return (
              <table key={item.id} className="table table-hover" style={{ borderBottom: '1px solid black' }}>
                <tbody>
                  <tr>
                    <td>
                      <label>№:</label> {index + 1}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label>№ Счетчика:</label> {item.chetch_number}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label>Место:</label> {item.position}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label>ФИО:</label>{' '}
                      <input
                        type="text"
                        name="fio"
                        value={draft.fio}
                        className="form-control mobi_inp"
                        autoComplete="off"
                        style={fieldStyle(item.id, 'fio')}
                        onChange={(event) => setField(item.id, 'fio', event.target.value)}
                        onBlur={() => onFieldBlur(item.id, 'fio')}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label>Логин:</label>{' '}
                      <input
                        type="text"
                        name="login"
                        value={draft.login}
                        className="form-control mobi_inp"
                        autoComplete="off"
                        style={fieldStyle(item.id, 'login')}
                        onChange={(event) => {
                          setField(item.id, 'login', event.target.value)
                          validateField(item.id, 'login', event.target.value)
                        }}
                        onBlur={() => onFieldBlur(item.id, 'login')}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label>Пароль:</label>{' '}
                      <input
                        type="text"
                        name="pass"
                        value={draft.pass}
                        className="form-control mobi_inp"
                        autoComplete="off"
                        style={fieldStyle(item.id, 'pass')}
                        onChange={(event) => {
                          setField(item.id, 'pass', event.target.value)
                          validateField(item.id, 'pass', event.target.value)
                        }}
                        onBlur={() => onFieldBlur(item.id, 'pass')}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label>Email:</label>{' '}
                      <input
                        type="email"
                        name="email"
                        value={draft.email}
                        className="form-control mobi_inp"
                        autoComplete="off"
                        style={fieldStyle(item.id, 'email')}
                        onChange={(event) => {
                          setField(item.id, 'email', event.target.value)
                          validateField(item.id, 'email', event.target.value)
                        }}
                        onBlur={() => onFieldBlur(item.id, 'email')}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label>Телефон:</label>{' '}
                      <input
                        type="tel"
                        name="tel"
                        className="phone form-control mobi_inp"
                        autoComplete="off"
                        value={draft.tel}
                        style={fieldStyle(item.id, 'tel')}
                        onChange={(event) => setField(item.id, 'tel', event.target.value)}
                        onBlur={() => onFieldBlur(item.id, 'tel')}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label>Кандидат: </label>
                      <input
                        type="checkbox"
                        className="mobi_checkbox"
                        name="candidate"
                        value="1"
                        checked={draft.candidate}
                        style={{ marginLeft: 10 }}
                        onChange={(event) => onFlagChange(item.id, 'candidate', event.target.checked)}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label>Рассылка квитанций:</label>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label>Email: </label>
                      <input
                        type="checkbox"
                        className="desc_checkbox"
                        name="rassl_email"
                        value="1"
                        checked={draft.rassl_email}
                        style={{ marginLeft: 10 }}
                        onChange={(event) => onFlagChange(item.id, 'rassl_email', event.target.checked)}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <input type="hidden" name="update_user" value={item.id} className="bt" readOnly />
                    </td>
                  </tr>
                </tbody>
              </table>
            )
          })}
        </div>
      </div>

      {/* confirm-модал шаблона: «Подтверждаете изменение данных?» */}
      {confirm !== null && (
        <Modal
          open
          // закрытие любым способом (Отмена / фон / Esc) = отказ от правки: возвращаем прежнее значение
          onClose={() => {
            confirm.abort()
            setConfirm(null)
          }}
          size="sm"
          footer={
            <>
              <a
                href="#"
                className="btn btn-primary yesBtn btn-sm"
                onClick={(event) => {
                  event.preventDefault()
                  const id = confirm.id
                  setConfirm(null)
                  void save(id)
                }}
              >
                Подтвердить
              </a>
              <button
                type="button"
                className="btn btn-secondary abortBtn btn-sm"
                onClick={() => {
                  confirm.abort()
                  setConfirm(null)
                }}
              >
                Отмена
              </button>
            </>
          }
        >
          {' '}
          Подтверждаете изменение данных?{' '}
        </Modal>
      )}
    </>
  )
}
